import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge, StatusBadge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useListControls } from '../../hooks/useListControls.js';
import { useOrganisationRoleNames } from '../../hooks/useRoleOptions.js';
import { api } from '../../services/api.js';
import {
  type EmploymentStatus,
  type Membership,
  type MembershipRole,
  membershipsApi,
} from '../../services/memberships.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;

// 046: supplier-employee roster. Admin manages (edit/delete/restore);
// supplier/host see a read-only own-org view (backend force-scopes; the
// include-deleted filter is admin-only both here and server-side).

const STATUSES: EmploymentStatus[] = ['active', 'inactive', 'terminated'];

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function MembershipListPage() {
  const ability = useAbility();
  const canManage = ability.can('manage', 'Membership');
  const queryClient = useQueryClient();

  const [supplierId, setSupplierId] = useState('');
  const [role, setRole] = useState('');
  // 367 (FR-011): the organisation roles on the Roles page (Host when the platform cannot say).
  const organisationRoles = useOrganisationRoleNames();
  const [status, setStatus] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // 162 (#21): a pager and a search box. `resetOn` carries this screen's four existing filters,
  // so any of them returning results to page 1 is one rule rather than four (FR-007).
  const { page, setPage, searchInput, setSearchInput, search } = useListControls({
    resetOn: [supplierId, role, status, includeDeleted],
  });

  const { data, isLoading, error } = useQuery<{ items: Membership[]; pages: number }>({
    queryKey: [
      ...RESOURCE_KEYS.membership.list,
      supplierId,
      role,
      status,
      includeDeleted,
      page,
      search,
    ],
    queryFn: () =>
      membershipsApi.list({
        supplier_id: supplierId || undefined,
        role: (role || undefined) as MembershipRole | undefined,
        employment_status: (status || undefined) as EmploymentStatus | undefined,
        include_deleted: includeDeleted,
        page,
        limit: PAGE_SIZE,
        // Only when there is something to filter by (FR-004).
        q: search || undefined,
      }),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  // supplier filter options — admin-only endpoint; suppliers are force-scoped
  // server-side anyway, so skip the fetch (and its 403) for them.
  const { data: suppliersData } = useQuery<{ items: Array<{ _id: string; company_name: string }> }>(
    {
      queryKey: ['suppliers-for-membership-filter'],
      queryFn: () => api.get('/suppliers'),
      enabled: canManage,
    },
  );

  const items = data?.items ?? [];

  async function removeMembership(id: string) {
    await membershipsApi.remove(id);
    await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.membership.list });
  }

  async function restoreMembership(id: string) {
    await membershipsApi.restore(id);
    await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.membership.list });
  }

  const columns: Column<Membership>[] = [
    {
      key: 'user',
      header: 'Employee',
      render: (m) =>
        m.user ? (
          <div>
            <div className="text-ink">{m.user.name || m.user.email}</div>
            <div className="text-xs text-steel">{m.user.email}</div>
          </div>
        ) : (
          <span className="text-steel italic">(deleted user)</span>
        ),
    },
    {
      key: 'supplier',
      header: 'Organization',
      render: (m) =>
        m.supplier ? (
          m.supplier.company_name
        ) : (
          <span className="text-steel italic">(deleted supplier)</span>
        ),
    },
    { key: 'role', header: 'Role', render: (m) => m.role },
    {
      key: 'employee_code',
      header: 'Code',
      render: (m) => m.employee_code ?? <span className="text-steel">—</span>,
    },
    {
      key: 'employment_status',
      header: 'Status',
      render: (m) => (
        <span className="flex flex-wrap items-center gap-1">
          <StatusBadge status={m.employment_status} />
          {m.deleted_at && <Badge variant="default">deleted</Badge>}
        </span>
      ),
    },
    {
      key: 'assigned_country',
      header: 'Country',
      render: (m) => m.assigned_country?.name ?? <span className="text-steel">—</span>,
    },
    { key: 'created_at', header: 'Since', render: (m) => formatDate(m.created_at) },
    {
      key: 'id',
      header: 'Actions',
      render: (m) => (
        <RowActions
          actions={[
            {
              key: 'view',
              label: canManage && !m.deleted_at ? 'Edit' : 'View',
              href: `/memberships/${m.id}`,
              icon:
                canManage && !m.deleted_at ? (
                  <Pencil className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                ),
            },
            canManage &&
              !m.deleted_at && {
                key: 'delete',
                label: 'Delete',
                variant: 'danger' as const,
                confirmMessage:
                  'Soft-delete this membership? The employee loses this organization’s access on their next request.',
                onClick: () => removeMembership(m.id),
                icon: <Trash2 className="h-4 w-4" />,
              },
            canManage &&
              Boolean(m.deleted_at) && {
                key: 'restore',
                label: 'Restore',
                confirmMessage: 'Restore this membership?',
                onClick: () => restoreMembership(m.id),
                icon: <RotateCcw className="h-4 w-4" />,
              },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Memberships" />

      <Card className="mb-4 flex flex-wrap items-center gap-3">
        {canManage && (
          <Select
            data-testid="membership-filter-supplier"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">All organizations</option>
            {(suppliersData?.items ?? []).map((s) => (
              <option key={s._id} value={s._id}>
                {s.company_name}
              </option>
            ))}
          </Select>
        )}
        <Select
          data-testid="membership-filter-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="">All roles</option>
          {organisationRoles.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
        <Select
          data-testid="membership-filter-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        {canManage && (
          <label className="flex items-center gap-2 text-sm text-slate">
            <input
              data-testid="membership-filter-deleted"
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            Include deleted
          </label>
        )}
      </Card>

      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search memberships by member email"
        testId="membership-search"
      />

      <DataTable
        columns={columns}
        data={items}
        loading={isLoading}
        keyExtractor={(m) => m.id}
        emptyMessage="No memberships found"
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
